/**
 * Redirect bookmarks from the retired hash-routed SPA.
 *
 * The old application addressed pages with a fragment (`/#/history`). A fragment never reaches
 * the server, so this cannot be a redirect in FastAPI — it has to happen in the browser, before
 * the router mounts, otherwise the user lands on the dashboard and silently loses where they
 * were going.
 */

/** Old hash route → new path. Query strings on the old route are preserved where meaningful. */
const ROUTE_MAP: Record<string, string> = {
  '/login': '/login',
  '/change-password': '/login/change-password',
  '/history': '/library/downloads',
  '/explore': '/explorer',
  '/settings': '/account/profile',
  '/feedback': '/support',
  '/admin': '/admin/users',
  '/admin-cookies': '/admin/integrations',
  '/admin-feedback': '/admin/feedback',
};

/**
 * Translate a legacy hash into a path, or return null when there is nothing to translate.
 * Exported for testing; `applyLegacyHashRedirect` is what the app calls.
 */
export function translateLegacyHash(hash: string): string | null {
  if (!hash.startsWith('#/')) return null;

  const withoutHash = hash.slice(1);
  const [route, query = ''] = withoutHash.split('?');
  const params = new URLSearchParams(query);

  // The editor carried its target in a query parameter; the new route puts it in the path.
  if (route === '/editor') {
    const projectId = params.get('project_id');
    return projectId ? `/projects/${projectId}/edit` : '/projects';
  }

  const mapped = ROUTE_MAP[route ?? ''];
  if (!mapped) return null;

  return query ? `${mapped}?${query}` : mapped;
}

/**
 * Rewrite the current URL if it is a legacy bookmark. Called once before the router mounts.
 */
export function applyLegacyHashRedirect(): void {
  const target = translateLegacyHash(window.location.hash);
  if (!target) return;
  window.history.replaceState(null, '', target);
}
